import { useState, useEffect, useCallback } from 'react';
import { Menu, Shield, Globe } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface AdminHeaderProps {
  onMenuClick: () => void;
}

const AdminHeader = ({ onMenuClick }: AdminHeaderProps) => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);

  const languages = [
    { code: 'en', name: t('english'), flag: '\u{1F1FA}\u{1F1F8}' },
    { code: 'es', name: t('spanish'), flag: '\u{1F1EA}\u{1F1F8}' },
    { code: 'fr', name: t('french'), flag: '\u{1F1EB}\u{1F1F7}' },
    { code: 'it', name: t('italian'), flag: '\u{1F1EE}\u{1F1F9}' },
    { code: 'ar', name: t('arabic'), flag: '\u{1F1F8}\u{1F1E6}' },
    { code: 'zh', name: t('chinese'), flag: '\u{1F1E8}\u{1F1F3}' },
  ];

  const currentLanguage = languages.find(l => l.code === i18n.language) || languages[0];

  const changeLanguage = useCallback((langCode: string) => {
    i18n.changeLanguage(langCode);
    setShowLanguageMenu(false);
  }, [i18n]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.admin-language-selector')) {
        setShowLanguageMenu(false);
      }
    };

    if (showLanguageMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLanguageMenu]);

  return (
    <header className="bg-gray-950 border-b border-gray-800 px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-600/20 border border-blue-500/30 rounded-lg">
            <Shield className="h-4 w-4 text-blue-400" />
            <span className="text-sm font-medium text-blue-400">{t('admin_header.admin_mode')}</span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative admin-language-selector">
            <button
              onClick={() => setShowLanguageMenu(!showLanguageMenu)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
            >
              <Globe className="h-4 w-4" />
              <span className="text-sm hidden sm:inline">{currentLanguage.flag}</span>
            </button>

            {showLanguageMenu && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-gray-900 border border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden">
                <div className="p-2">
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-700 mb-2">
                    {t('language')}
                  </div>
                  {languages.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
                        i18n.language === lang.code
                          ? 'bg-blue-600/15 text-blue-400 font-medium'
                          : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                      }`}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      <span>{lang.name}</span>
                      {i18n.language === lang.code && (
                        <span className="ml-auto w-2 h-2 rounded-full bg-blue-400" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center ring-2 ring-blue-400/50">
              <span className="text-sm font-medium text-white">
                {user?.email?.[0]?.toUpperCase() || 'A'}
              </span>
            </div>
            <div className="hidden sm:block">
              <p className="text-sm font-medium text-white">
                {user?.email?.split('@')[0]}
              </p>
              <p className="text-xs text-blue-400 font-medium">{t('admin_header.mega_admin')}</p>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;
